import View from "../framework/View";
import State from "../../State";
import GridInversionOfControl from "../../model/grid/GridInversionOfControl";
import GridTile from "../../model/grid/GridTile";
import Sprite from "../../model/sprite/Sprite";
import Vector from "../../model/math/Vector";
import { Settings } from "../../Settings";
import Character from "../../model/character/Character";
import { PageIds } from "../PageIds";
import Projection from "../../Projection";
import Sprites from "../../model/sprite/Sprites";
import TileDamageSpriteOverlay from "../../model/sprite/TileDamageSpriteOverlay";
import OreSpriteOverlay from "../../model/sprite/OreSpriteOverlay";
import WorldGrid from "../../model/grid/WorldGrid";
import PartyCharacter from "../../model/character/PartyCharacter";
import BigNum from "../../model/math/BigNum";
import NumberFormatter from "../../util/NumberFormatter";
import PartyTarget from "../../model/character/PartyTarget";
import VectorField from "../../model/grid/VectorField";
import Crew from "../../model/crew/Crew";
import Grid from "../../model/grid/Grid";
import InfoTextProcessor from "../../model/info/InfoTextProcessor";
import SpriteAnimation from "../../model/sprite/SpriteAnimation";
import Effect from "../../model/effect/Effect";
import CrewBrain from "../../model/crew/CrewBrain";
import Page from "../framework/Page";
import UserInput from "../../input/UserInput";
import SkillType from "../../model/skillTree/SkillType";
import { TileTypeRarity } from "../../model/grid/TileTypeRarity";
import { VectorFieldScores } from "../../model/path/VectorFieldScores";
import PerformanceMetrics from "../../model/performance/PerformanceMetrics";
import GridRegion from "../../model/grid/GridRegion";

export default class CanvasView extends View {
    /**
     * @param {string} parentId
     * @param {State} state
     * @param {UserInput} userInput
     */
    constructor(parentId, state, userInput) {
        super(parentId, PageIds.GAME_CANVAS);

        /**
         * @type {HTMLCanvasElement}
         */
        this.canvas = null; // Created in initializeView()

        /**
         * @private
         * @type {CanvasRenderingContext2D}
         */
        this.context = null;  // Created in initializeView()

        /**
         * @constant
         * @type {UserInput}
         */
        this.userInput = userInput;

        /**
         * @private
         * @constant
         * @type {Projection}
         */
        this.projection = state.projection;

        /**
         * @constant
         * @type {Sprites}
         */
        this.sprites = state.sprites;

        /**
         * @constant
         * @type {TileDamageSpriteOverlay}
         */
        this.tileDamageSpriteOverlay = this.sprites.tileDamageSpriteOverlay;

        /**
         * @constant
         * @type {OreSpriteOverlay}
         */
        this.oreSpriteOverlay = this.sprites.oreSpriteOverlay;

        /**
         * @constant
         * @type {SpriteAnimation}
         */
        this.upgradeSpriteOverlay = this.sprites.tileSpriteOverlaySpritesheet.upgradeSpriteOverlay;

        /**
         * @constant
         * @type {Crew}
         */
        this.crew = state.crew;

        /**
         * @constant
         * @type {PartyTarget}
         */
        this.partyTarget = state.partyTarget;

        /**
         * @private
         * @constant
         * @type {State}
         */
        this.state = state;

        /**
         * @private
         * @constant
         * @type {WorldGrid}
         */
        this.worldGrid = state.worldGrid;

        /**
         * @constant
         * @type {VectorField}
         */
        this.vectorField = state.vectorField;

        /**
         * @constant
         * @type {InfoTextProcessor}
         */
        this.infoTextProcessor = state.infoTextProcessor;


        /**
         * @private
         * @type {number}
         */
        this.spriteSize = 0;
        /**
         * @private
         * @type {number}
         */
        this.scaledSize = 0;

        /**
         * @constant
         * @type {Vector}
         */
        this.screenCoordinate = new Vector(0, 0);
        /**
         * @constant
         * @type {Vector}
         */
        this.screenCoordinate2 = new Vector(0, 0);

        /**
         * @constant
         * @type {Vector}
         */
        this.worldCoordinate = new Vector(0, 0);

        /**
         * @type {BigNum}
         */
        this.tileHealthNum = new BigNum(0);

        /**
         * @constant
         * @type {CrewBrain}
         */
        this.crewBrain = state.crewBrain;

        const thisRef = this;
        
        /**
         * @type {Function}
         * @param {GridRegion} region 
         */
        this.renderRegionBorderDebugCallback = (region) => {
            if (region.vectorFieldNumber != this.vectorField.vectorFieldNumber) {
                return;
            }

            thisRef.renderRegionBorder(region);
        };

        /**
         * @type {Function}
         * @param {GridTile} tile 
         */
        this.renderTilesCallback = (tile) => {
            thisRef.renderTile(tile);
        };

        /**
         * @type {Function}
         * @param {GridTile} tile 
         */
        this.renderWireframeTilesCallback = (tile) => {
            thisRef.renderTileWireframe(tile);
        };

        /**
         * @type {Function}
         * @param {Grid} grid
         */
        this.renderMonstersCallback = (grid) => {
            thisRef.renderMonsterCharacters(grid);
        }

    }

    /**
     * @override
     */
    initializeView() {
        // <canvas id="gameCanvas" tabindex="1" class="game-canvas">Your browser does not support the HTML5 canvas.</canvas>

        const parent = Page.getElement(this.parentId);
        this.canvas = Page.createElement('canvas', parent, PageIds.GAME_CANVAS, 'game-canvas');

        this.context = this.canvas.getContext('2d');

        // Set the canvas to full screen.
        this.canvas.width = window.innerWidth;
        this.canvas.height = window.innerHeight;

        // Attach the user input listeners to the canvas
        this.userInput.attachListeners(document, this.canvas);

        // The projection needs to know about the main canvas.
        this.projection.setCanvas(this.canvas);

        this.setInitialVisibility(true);

        super.initializeView();
    }

    /**
     * @returns {HTMLCanvasElement}
     */
    getCanvas() {
        return this.canvas;
    }

    /**
     * @override
     */
    updateViewContents() {
        // Clear the background.
        this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);

        this.context.font = '12pt helvetica';

        this.context.imageSmoothingEnabled = false;

        PerformanceMetrics.startTime('Render-total');
        this.renderWorld();
        PerformanceMetrics.endTime('Render-total');
    }

    /**
     * @private
     */
    renderWorld() {

        ////////////////////////////////////////////////////////////////
        // Scaling
        ////////////////////////////////////////////////////////////////

        this.spriteSize = this.sprites.tileSpritesheet.spriteSize;
        this.scaledSize = this.spriteSize * this.projection.zoom;

        //const thisRef = this;

        ////////////////////////////////////////////////////////////////
        // Render the grid
        ////////////////////////////////////////////////////////////////

        // GridInversionOfControl.allVisibleTiles(this.worldGrid, this.projection, 
        //     /**
        //      * @param {GridTile} tile 
        //      */
        //     function(tile) {
        //         thisRef.renderTile(tile);
        //     }
        // );

        
        PerformanceMetrics.startTime('Render-blocks');
        if (this.state.gameOptions.wireframeModeEnabled) { 
            GridInversionOfControl.allVisibleTiles(this.worldGrid, this.projection, this.renderWireframeTilesCallback);
        }
        else {
            GridInversionOfControl.allVisibleTiles(this.worldGrid, this.projection, this.renderTilesCallback);
        }
        PerformanceMetrics.endTime('Render-blocks');

        ////////////////////////////////////////////////////////////////
        // Info Text
        ////////////////////////////////////////////////////////////////

        this.renderInfoText();

        ////////////////////////////////////////////////////////////////
        // Render the characters
        ////////////////////////////////////////////////////////////////

        GridInversionOfControl.allVisibleGrids(this.worldGrid, this.projection, this.renderMonstersCallback);
        //     /**
        //      * @param {Grid} grid 
        //      */
        //     function(grid) {
        //         thisRef.renderMonsterCharacters(grid);
        //     }
        // );


        //this.renderGridBorders();
        if (this.state.gameOptions.crewBrainDebugEnabled) {
            //////this.renderVectorFieldBorder();
            //this.renderVectorFieldBorder2(17);
            //this.renderVectorFieldBorder2(25);
            this.renderRegionBordersDebug();
            this.renderVectorFieldTargets();
        }

        //////////////////////////////////////////////////////////////
        // Render tile depth along left edge
        //////////////////////////////////////////////////////////////

        if (this.state.gameOptions.depthColumnRendered) {
            this.renderTileDepths();
        }

        if (this.state.gameOptions.effectsRenderingEnabled) {
            PerformanceMetrics.startTime('Render-effects');
            this.renderEffects();
            this.renderSkills();
            PerformanceMetrics.endTime('Render-effects');
        }

        PerformanceMetrics.startTime('Render-crew');
        this.renderCrew();
        PerformanceMetrics.endTime('Render-crew');

        if (this.state.gameOptions.crewBrainDebugEnabled) {
            this.renderPaths();
        }


        /////////////////////////////////////////////
        // if (this.character.position.tile) {
        //     this.projection.worldToScreen(this.character.position.worldCoordinateOrigin, this.screenCoordinate);
        //     this.context.fillStyle = "white";
        //     this.context.fillText('(' + this.character.position.tile.getTileCol() + ', ' + this.character.position.tile.getTileRow() + ')', 
        //         this.screenCoordinate.x + 15, this.screenCoordinate.y + 70);
        // }
        /////////////////////////////////////////////


        //////////////////////////////////////////////////////////////
        // Render the FPS
        //////////////////////////////////////////////////////////////

        if (this.state.gameOptions.displayFps) {
            this.context.fillStyle = "#FFFA";
            this.context.fillText("FPS: " + this.state.currentFps, 270, this.canvas.height - 100);
        }
        ///this.context.fillText("Turn: " + this.state.gameTime.turnNumber, (this.canvas.width / 2) + 150, this.canvas.height - 145);


    }

    renderPaths() {
        const miners = this.crew.miners;

        for (let i = 0; i < miners.length; i++) {
            this.renderPath(miners[i]);
        }

        ///////////////////////////
        // TURN ACTIONS
        ///////////////////////////
        
        // this.context.fillStyle = '#AAA'; 
        // for (let i = 0; i < miners.length; i++) {
        //     const miner = miners[i];

        //     this.screenCoordinate.copy(miner.position.worldCoordinateOrigin);
        //     this.projection.worldToScreen(this.screenCoordinate, this.screenCoordinate);
        //     this.context.fillText(miner.debugText, this.screenCoordinate.x + 5, this.screenCoordinate.y - 5);


        //     // if (miner.turnAction) {
        //     //     continue;
        //     // }

        //     // this.screenCoordinate.copy(miner.position.worldCoordinateOrigin);
        //     // this.projection.worldToScreen(this.screenCoordinate, this.screenCoordinate);

        //     // const turnActionText = 'NOTHING';
        //     // this.context.fillText(turnActionText, this.screenCoordinate.x + 5, this.screenCoordinate.y - 5);
        // }
    }


    renderPath(miner) {
        if (!miner.position.tile) {
            return;
        }
        // Ignore empty or super short paths
        if (miner.position.pathPlan.length < 2) {
            return;
        }

        this.context.strokeStyle = miner.target.invalidated ? "#F114" : "#F834";
        this.context.lineWidth = 4;

        this.context.beginPath();

        for (let i = 0; i < miner.position.pathPlan.length; i++) {
            const planStep = miner.position.pathPlan[i];

            this.screenCoordinate.copy(planStep.startTile.origin);
            this.screenCoordinate.addXY(Settings.tile.halfSize, Settings.tile.halfSize);
            this.projection.worldToScreen(this.screenCoordinate, this.screenCoordinate);
            this.context.moveTo(this.screenCoordinate.x, this.screenCoordinate.y);

            this.screenCoordinate.copy(planStep.endTile.origin);
            this.screenCoordinate.addXY(Settings.tile.halfSize, Settings.tile.halfSize);
            this.projection.worldToScreen(this.screenCoordinate, this.screenCoordinate);
            this.context.lineTo(this.screenCoordinate.x, this.screenCoordinate.y);
        }

        this.context.stroke();
    }

    /**
     * @private
     */
    renderCrew() {

        const miners = this.crew.miners;

        // The overlay sprites are 3x larger than the regular sprites.
        this.spriteSize = this.sprites.itemOverlaySpritesheet.spriteSize;
        this.scaledSize = this.spriteSize * this.projection.zoom;

        // Render the thrusters first so that they do not cover miners
        for (let i = 0; i < miners.length; i++) {
            this.renderThruster(miners[i]);
        }

        this.spriteSize = this.sprites.tileSpritesheet.spriteSize;
        this.scaledSize = this.spriteSize * this.projection.zoom;

        for (let i = 0; i < miners.length; i++) {
            this.renderCharacter(miners[i], false);
        }

        // The overlay sprites are 3x larger than the regular sprites.
        this.spriteSize = this.sprites.itemOverlaySpritesheet.spriteSize;
        this.scaledSize = this.spriteSize * this.projection.zoom;

        for (let i = 0; i < miners.length; i++) {
            this.renderItemOverlay(miners[i]);
        }

        //////////////////////////////////////////////////////////////

        // this.context.fillStyle = "white";
        // for (let i = 0; i < miners.length; i++) {
        //     const miner = miners[i];

        //     const targetTile = miner.target.targetTile;
        //     const mineTile = miner.target.mineTile;

        //     const turnAction = miner.turnAction;

        //     let t = '[' + i + '] ' + (targetTile ? 'T' : 'X') + (mineTile ? 'M' : 'x') + (turnAction ? turnAction.toString() : 'N');

        //     this.projection.worldToScreen(miner.position.worldCoordinateOrigin, this.screenCoordinate);
        //     this.context.fillText(t, this.screenCoordinate.x + 5, this.screenCoordinate.y + 5);

        //     if (targetTile) {
        //         this.context.strokeStyle = "white";
        //         this.context.beginPath();
        //         this.context.moveTo(this.screenCoordinate.x + 5, this.screenCoordinate.y + 5);

        //         this.projection.worldToScreen(targetTile.origin, this.screenCoordinate2);

        //         this.context.lineTo(this.screenCoordinate2.x + 5, this.screenCoordinate2.y + 5);
        //         this.context.stroke();
        //     }

        //     if (mineTile) {
        //         this.context.strokeStyle = "yellow";
        //         this.context.beginPath();
        //         this.context.moveTo(this.screenCoordinate.x + 5, this.screenCoordinate.y + 5);

        //         this.projection.worldToScreen(mineTile.origin, this.screenCoordinate2);

        //         this.context.lineTo(this.screenCoordinate2.x + 5, this.screenCoordinate2.y + 5);
        //         this.context.stroke();
        //     }
        // }

    }

    // /**
    //  * @private
    //  * @param {GridTile} tile 
    //  */
    // renderTileNeighborScores(tile) {

    //     const neighbors = tile.neighbors;

    //     this.screenCoordinate.copy(tile.origin);
    //     this.screenCoordinate.addXY(Settings.tile.halfSize, Settings.tile.halfSize);
    //     ////////this.projection.worldToScreen(this.screenCoordinate, this.screenCoordinate);

    //     for (let i = 0; i < neighbors.length; i++) {
    //         const neighbor = neighbors[i];
    //         if (!neighbor) {
    //             continue;
    //         }

    //         // If the neighbor is known, then it provides no bonus to this tile.
    //         if (neighbor.lighting.everVisibleToCharacter || neighbor.tileType === SpecialTileTypes.SKY) {
    //             continue;
    //         }

    //         this.screenCoordinate2.copy(neighbor.origin);
    //         this.screenCoordinate2.subtract(tile.origin);
    //         this.screenCoordinate2.scale(0.5);
    //         this.screenCoordinate2.add(this.screenCoordinate);
    //         ///////this.screenCoordinate2.addXY(Settings.tile.halfSize, Settings.tile.halfSize);

    //         this.projection.worldToScreen(this.screenCoordinate2, this.screenCoordinate2);
    
    //         // Unseen neighbor tiles that are open are caverns.
    //         if (neighbor.open) {
    //             //this.context.fillStyle = "white";
    //             this.context.fillRect(this.screenCoordinate2.x - 3, this.screenCoordinate2.y - 3, 6, 6);
    //         }
    //         else {
    //             //this.context.fillStyle = "yellow";
    //             this.context.fillRect(this.screenCoordinate2.x - 2, this.screenCoordinate2.y - 2, 4, 4);
    //         }
    //     }
    // }
    
    renderInfoText() {
        const infoTextArray = this.infoTextProcessor.infoTextArray;
        if (infoTextArray.length === 0) {
            return;
        }

        this.context.fillStyle = "white";

        for (let i = 0; i < infoTextArray.length; i++) {
            const infoText = infoTextArray[i];
            this.context.fillText(infoText.text, infoText.textX, infoText.textY);
        }
    }

    renderRegionBordersDebug() {

        if (!this.state.vectorField.centerTile) {
            return;
        }

        const startTileOrigin = this.state.vectorField.centerTile.origin;

        const mineTileRadius = 25 * Settings.tile.size; // common tile radius is 17

        // The mine tile radius is larger than the common tile radius.
        GridInversionOfControl.allNearbyRegions(this.worldGrid, this.projection, startTileOrigin, mineTileRadius, this.renderRegionBorderDebugCallback);
    }

    /**
     * @param {GridRegion} region 
     */
    renderRegionBorder(region) {

        this.screenCoordinate.copy(region.origin);
        this.projection.worldToScreen(this.screenCoordinate, this.screenCoordinate); // top left

        this.screenCoordinate2.copy(region.origin);
        this.screenCoordinate2.addXY(Settings.grid.regionPixelWidth, Settings.grid.regionPixelHeight);
        this.projection.worldToScreen(this.screenCoordinate2, this.screenCoordinate2); // bottom right

        const widthA = Math.abs(this.screenCoordinate2.x - this.screenCoordinate.x);
        const heightA = Math.abs(this.screenCoordinate2.y - this.screenCoordinate.y);

        this.context.strokeStyle = '#22222288';
        this.context.lineWidth = 2;

        this.context.strokeRect(this.screenCoordinate.x, this.screenCoordinate.y, widthA, heightA);
    }


    renderVectorFieldTargets() {
     
        //////this.vectorField.priorityMiningCandidates
        
        this.context.fillStyle = '#FF0000';
        this.renderTargetList(this.vectorField.priorityMiningCandidates);

        this.context.fillStyle = '#FFFF00';
        this.renderTargetList(this.vectorField.currencyMiningCandidates);

        this.context.fillStyle = '#FF00FF';
        this.renderTargetList(this.vectorField.commonMiningCandidates);
        

        for (let i = 0; i < this.crew.miners.length; i++) {
            const miner = this.crew.miners[i];
            const targetTile = miner.target.targetTile;
            const mineTile = miner.target.mineTile;

            this.renderMineTarget(miner.position.tile, targetTile, mineTile);
        }

        /////this.screenCoordinate.copy(this.vectorField.centerTile.origin);

        //////////////////////////////////////////
        // Center tile
        //////////////////////////////////////////

        const vectorFieldCenterTile = this.vectorField.centerTile;
        if (vectorFieldCenterTile) {
            this.screenCoordinate.copy(vectorFieldCenterTile.origin);
            this.screenCoordinate.addXY(4, 4);

            this.screenCoordinate2.copy(vectorFieldCenterTile.origin);
            this.screenCoordinate2.addXY(Settings.tile.size - 4, Settings.tile.size - 4);

            this.projection.worldToScreen(this.screenCoordinate, this.screenCoordinate);
            this.projection.worldToScreen(this.screenCoordinate2, this.screenCoordinate2);

            this.context.fillStyle = '#FFFFFF';
            this.context.fillRect(this.screenCoordinate.x, this.screenCoordinate.y, this.screenCoordinate2.x - this.screenCoordinate.x, this.screenCoordinate2.y - this.screenCoordinate.y);
        }

        const crewCenterTile = this.crew.centerTile;
        if (crewCenterTile) {
            this.screenCoordinate.copy(crewCenterTile.origin);
            this.screenCoordinate.addXY(4, 4);

            this.screenCoordinate2.copy(crewCenterTile.origin);
            this.screenCoordinate2.addXY(Settings.tile.size - 4, Settings.tile.size - 4);

            this.projection.worldToScreen(this.screenCoordinate, this.screenCoordinate);
            this.projection.worldToScreen(this.screenCoordinate2, this.screenCoordinate2);

            this.context.fillStyle = '#00FFFF';
            this.context.fillRect(this.screenCoordinate.x, this.screenCoordinate.y, this.screenCoordinate2.x - this.screenCoordinate.x, this.screenCoordinate2.y - this.screenCoordinate.y);
        }

        const destinationTiles = this.vectorField.destinationTileCandidates;
        if (destinationTiles.length > 0) {
            this.context.strokeStyle = '#FF02';
            this.context.lineWidth = 2;

            for (let i = 0; i < destinationTiles.length; i++) {
                const dt = destinationTiles[i];

                // if we find a null destination tile, then we are probably at the end of them.
                if (!dt) {
                    break;
                }

                this.screenCoordinate.copy(dt.origin);
                this.screenCoordinate.addXY(4, 4);
    
                this.screenCoordinate2.copy(dt.origin);
                this.screenCoordinate2.addXY(Settings.tile.size - 4, Settings.tile.size - 4);
    
                this.projection.worldToScreen(this.screenCoordinate, this.screenCoordinate);
                this.projection.worldToScreen(this.screenCoordinate2, this.screenCoordinate2);
    
                this.context.strokeRect(this.screenCoordinate.x, this.screenCoordinate.y, this.screenCoordinate2.x - this.screenCoordinate.x, this.screenCoordinate2.y - this.screenCoordinate.y);
            }
        }

        //////////////////////////////////////////
        // Mine Control Properties
        //////////////////////////////////////////

        this.context.fillStyle = "#888";
        this.context.fillText('Tile health: ' + (this.state.mineControl.debugTileHealthNegligible ? 'negligible' : 'normal'), 250, this.canvas.height - 280);
        this.context.fillText('Search grid: ' + (this.state.mineControl.debugSearchGridSmall ? 'small' : 'normal'), 250, this.canvas.height - 260);
        this.context.fillText('Premium tiles: ' + (this.state.mineControl.debugIgnorePremium ? 'ignored' : 'targeted'), 250, this.canvas.height - 240);
        this.context.fillText('Common tiles: ' + (this.state.mineControl.debugIgnoreCommon ? 'ignored' : 'targeted'), 250, this.canvas.height - 220);
        this.context.fillText('Goal description: ' + (this.state.mineControl.debugGoalDescription), 250, this.canvas.height - 200);
        

    }

    /**
     * @private
     * @param {GridTile} tile 
     * @param {GridTile} targetTile 
     * @param {GridTile} mineTile 
     */
    renderMineTarget(tile, targetTile, mineTile) {
        if (!tile) {
            return;
        }

        ///////////////////////////////
        ///////////////////////////////

        if (mineTile) {
            this.screenCoordinate.copy(mineTile.origin);
            this.screenCoordinate.addXY(Settings.tile.quarterSize + 2, Settings.tile.quarterSize + 2);

            this.screenCoordinate2.copy(mineTile.origin);
            this.screenCoordinate2.addXY(Settings.tile.threeQuarterSize - 2, Settings.tile.threeQuarterSize - 2);

            this.projection.worldToScreen(this.screenCoordinate, this.screenCoordinate);
            this.projection.worldToScreen(this.screenCoordinate2, this.screenCoordinate2);

            this.context.fillStyle = '#4444FF';
            this.context.fillRect(this.screenCoordinate.x, this.screenCoordinate.y, this.screenCoordinate2.x - this.screenCoordinate.x, this.screenCoordinate2.y - this.screenCoordinate.y);
            
            //////this.renderLineDebug(tile, mineTile, '#2F2');
        }

        if (targetTile) {
            this.screenCoordinate.copy(targetTile.origin);
            this.screenCoordinate.addXY(Settings.tile.quarterSize + 2, Settings.tile.quarterSize + 2);

            this.screenCoordinate2.copy(targetTile.origin);
            this.screenCoordinate2.addXY(Settings.tile.threeQuarterSize - 2, Settings.tile.threeQuarterSize - 2);

            this.projection.worldToScreen(this.screenCoordinate, this.screenCoordinate);
            this.projection.worldToScreen(this.screenCoordinate2, this.screenCoordinate2);

            this.context.fillStyle = '#FF4444';
            this.context.fillRect(this.screenCoordinate.x, this.screenCoordinate.y, this.screenCoordinate2.x - this.screenCoordinate.x, this.screenCoordinate2.y - this.screenCoordinate.y);

            this.renderLineDebug(tile, targetTile, '#AAF4');
        }
    }

    /**
     * @private
     * @param {GridTile} startTile 
     * @param {GridTile} endTile 
     * @param {string} color 
     */
    renderLineDebug(startTile, endTile, color) {
        this.context.strokeStyle = color; // miner.target.invalidated ? "#F114" : "#F834";
        this.context.lineWidth = 4;

        this.context.beginPath();

        this.screenCoordinate.copy(startTile.origin);
        this.screenCoordinate.addXY(Settings.tile.halfSize, Settings.tile.halfSize);
        this.projection.worldToScreen(this.screenCoordinate, this.screenCoordinate);
        this.context.moveTo(this.screenCoordinate.x, this.screenCoordinate.y);

        this.screenCoordinate.copy(endTile.origin);
        this.screenCoordinate.addXY(Settings.tile.halfSize, Settings.tile.halfSize);
        this.projection.worldToScreen(this.screenCoordinate, this.screenCoordinate);
        this.context.lineTo(this.screenCoordinate.x, this.screenCoordinate.y);

        this.context.stroke();
    }

    /**
     * @private
     * @param {GridTile[]} targets 
     */
    renderTargetList(targets) {
    
        for (let i = 0; i < targets.length; i++) {
            const tile = targets[i];

            this.screenCoordinate.copy(tile.origin);
            this.screenCoordinate.addXY(Settings.tile.quarterSize, Settings.tile.quarterSize);

            this.screenCoordinate2.copy(tile.origin);
            this.screenCoordinate2.addXY(Settings.tile.threeQuarterSize, Settings.tile.threeQuarterSize);

            this.projection.worldToScreen(this.screenCoordinate, this.screenCoordinate);
            this.projection.worldToScreen(this.screenCoordinate2, this.screenCoordinate2);

            this.context.fillRect(this.screenCoordinate.x, this.screenCoordinate.y, this.screenCoordinate2.x - this.screenCoordinate.x, this.screenCoordinate2.y - this.screenCoordinate.y);
        }
    }

    renderGridBorders() {
        this.context.strokeStyle = '#11111122';
        this.context.lineWidth = 1;

        const scaledGridSize = this.spriteSize * this.projection.zoom * Settings.grid.gridTileWidth;

        const grids = this.worldGrid.grids;
        for (let i = 0; i < grids.length; i++) {
            const grid = grids[i];
            if (this.projection.isVisibleGridBox(grid)) {

                this.projection.worldToScreen(grid.origin, this.screenCoordinate);

                this.context.strokeRect(this.screenCoordinate.x, this.screenCoordinate.y, scaledGridSize, scaledGridSize);
            }
        }
    }

    renderTileDepths() {

        const panelWidth = 190; // 80;

        /////////////////////////////////////////////
        // Render partially transparent rectangle
        /////////////////////////////////////////////

        this.context.fillStyle = "#22222211";
        this.context.fillRect(0, 0, panelWidth, this.canvas.height);

        // grab tile at world center, use that as starting point
        const centerRowTile = this.worldGrid.findGridTile(this.projection.gridCenter);
        if (!centerRowTile) {
            return;
        }
        
        // Move down 1 tile because the tile origin is the top left, and we want it to look like
        // its the bottom left when we render on the canvas.

        this.context.fillStyle = '#666666FF'; // 'white'
        let workingTile = centerRowTile;

        this.renderTileDepth(workingTile);

        this.renderTileHealth(workingTile);

        /////////////////////////////////////
        // Rows above center row
        /////////////////////////////////////

        workingTile = centerRowTile.getNeighborTop();
        while (workingTile && this.projection.isVisibleGridBox(workingTile)) {

            this.renderTileDepth(workingTile);

            this.renderTileHealth(workingTile);

            workingTile = workingTile.getNeighborTop();
        }

        /////////////////////////////////////
        // Rows below center row
        /////////////////////////////////////

        workingTile = centerRowTile.getNeighborBottom();
        while (workingTile && this.projection.isVisibleGridBox(workingTile)) {
            this.renderTileDepth(workingTile);

            this.renderTileHealth(workingTile);

            workingTile = workingTile.getNeighborBottom();
        }

        /////////////////////////////////
        // Labels at the bottom
        /////////////////////////////////

        this.context.fillStyle = "#111111EE";
        this.context.fillRect(0, this.canvas.height - 30, panelWidth, 30);

        this.context.fillStyle = '#666666FF'; // 'light grey'
        this.context.fillText('Depth', 5, this.canvas.height - 10);
        this.context.fillText('Health', 100, this.canvas.height - 10);

    }

    /**
     * @private
     * @param {GridTile} tile 
     */
    renderTileDepth(tile) {
        const panelWidth = 80;

        
        let displayRow = tile.getTileRow();

        // this.screenCoordinate.copy(tile.origin);
        // this.screenCoordinate.y += Settings.tile.size;
        // this.projection.worldToScreen(this.screenCoordinate, this.screenCoordinate);

        if (displayRow === this.partyTarget.targetRow) {
            ////////////////////////////
            // Target Row Display
            ////////////////////////////

            this.screenCoordinate.copy(tile.origin);
            this.screenCoordinate.y += Settings.tile.size;
            this.projection.worldToScreen(this.screenCoordinate, this.screenCoordinate);
    
            this.context.fillStyle = '#FF8822FF'; // orange
            this.context.fillRect(0, this.screenCoordinate.y, panelWidth, 3);
            this.context.fillText('' + this.partyTarget.targetRow + ' target', 5, this.screenCoordinate.y - 5);
        }
        else if (displayRow % 5 === 0) {
            this.screenCoordinate.copy(tile.origin);
            this.screenCoordinate.y += Settings.tile.size;
            this.projection.worldToScreen(this.screenCoordinate, this.screenCoordinate);
    
            this.context.fillStyle = '#666666FF'; // 'light grey'
            this.context.fillRect(0, this.screenCoordinate.y, panelWidth, 2);
            this.context.fillText('' + displayRow, 5, this.screenCoordinate.y - 5);
        }

    }

    /**
     * @private
     * @param {GridTile} tile 
     */
    renderTileHealth(tile) {

        const rowNumber = tile.getTileRow();
        if (rowNumber < 0) {
            return;
        }

        this.screenCoordinate.copy(tile.origin);
        this.screenCoordinate.y += Settings.tile.size;
        this.projection.worldToScreen(this.screenCoordinate, this.screenCoordinate);

        const layerDescription = tile.tileType.layerDescription;

        /////const tileType = layerDescription.primaryTileType;

        layerDescription.calculateTileHealth(rowNumber, this.tileHealthNum);

        /////TileHealthCalculator.calculateTileHealth(rowNumber, tileType, this.tileHealthNum);

        this.context.fillStyle = '#666666FF'; // 'white'
        this.context.fillText(NumberFormatter.formatBigNum(this.tileHealthNum), 100, this.screenCoordinate.y - 5);
    }

    /**
     * @private
     * @param {Grid} grid 
     */
    renderMonsterCharacters(grid) {
        const characters = grid.characters;
        for (let i = 0; i < characters.length; i++) {
            if (characters[i].isMonster()) {
                this.renderCharacter(characters[i], true);
            }
        }
    }

    /**
     * @private
     * @param {Character} character
     * @param {boolean} enforceLighting Only render if visible.
     */
    renderCharacter(character, enforceLighting) {
        const tile = character.position.tile;
        if (!tile) {
            return;
        }

        // Make sure the character is on the screen.
        if (!this.projection.isVisible(character.position.worldCoordinateOrigin, Settings.tile.size)) {
            return;
        }

        if (enforceLighting) {
    
            // Don't render characters if we have never seen the tile they are on, or if the tile has no lighting.
            const lighting = tile.lighting;
            if (enforceLighting && !lighting.everVisibleToCharacter) {
                return;
            }

            // Shadow alpha:
            // a = 0: The tile is completely in the shadows and should be black.
            // a = 1: The tile is completely lighted and has no shadow.

            // Make sure the tile isn't completely black
            if (tile.tileType.lightingSupported && lighting.shadowColor.a === 0) {
                return;
            }
        }

        const sprite = character.getCharacterSprite();

        if (character.position.facingLeft) {
            this.projection.worldToScreen(character.position.worldCoordinateOrigin, this.screenCoordinate);
            this.renderSprite(sprite, this.screenCoordinate);
        }
        else {
            // All character sprites are facing left by default.
            // If the character is facint right, then we need to horizontally flip the sprite image.

            this.worldCoordinate.copy(character.position.worldCoordinateOrigin);
            this.worldCoordinate.addXY(this.spriteSize, 0); // Shift the coordinate to the right by one sprite width.

            this.projection.worldToScreen(this.worldCoordinate, this.screenCoordinate);
            
            this.context.save();
            this.context.translate(this.screenCoordinate.x, this.screenCoordinate.y);
            this.context.scale(-1, 1);

            // Since we've translated the context to the coordinate, we need to render the sprite at the origin.
            this.screenCoordinate.set(0, 0);
            this.renderSprite(sprite, this.screenCoordinate);
            this.context.restore();
        }
    }

    /**
     * @private
     * @param {Character} character
     */
    renderItemOverlay(character) {
        if (!this.projection.isVisible(character.position.worldCoordinateOrigin, Settings.tile.size)) {
            return;
        }
        this.renderOverlaySprite(character, character.getItemOverlaySprite());
    }

    /**
     * @private
     * @param {PartyCharacter} character
     */
    renderThruster(character) {
        if (!this.projection.isVisible(character.position.worldCoordinateOrigin, Settings.tile.size)) {
            return;
        }
        this.renderOverlaySprite(character, character.getThrusterOverlaySprite());
    }

    /**
     * @private
     * @param {Character} character
     * @param {Sprite} sprite 
     */
    renderOverlaySprite(character, sprite) {
        // The overlay sprites is 3x the size of the character sprite.
        // Offset the sprite by 1 tile, in both x and y directions, to center it on the character.

        if (!sprite) {
            return;
        }

        if (character.position.facingLeft) {
            // Offset the sprite by 1 tile
            this.worldCoordinate.copy(character.position.worldCoordinateOrigin);
            this.worldCoordinate.subtractXY(Settings.tile.size, Settings.tile.size);

            this.projection.worldToScreen(this.worldCoordinate, this.screenCoordinate);
            this.renderSprite(sprite, this.screenCoordinate);
        }
        else {
            // All character sprites are facing left by default.
            // If the character is facint right, then we need to horizontally flip the sprite image.

            this.worldCoordinate.copy(character.position.worldCoordinateOrigin);
            this.worldCoordinate.subtractXY(Settings.tile.size, Settings.tile.size);

            this.worldCoordinate.addXY(this.spriteSize, 0); // Shift the coordinate to the right by one sprite width.

            this.projection.worldToScreen(this.worldCoordinate, this.screenCoordinate);

            this.context.save();
            this.context.translate(this.screenCoordinate.x, this.screenCoordinate.y);
            this.context.scale(-1, 1);

            // Since we've translated the context to the coordinate, we need to render the sprite at the origin.
            this.screenCoordinate.set(0, 0);
            this.renderSprite(sprite, this.screenCoordinate);
            this.context.restore();
        }
    }

    /**
     * @private
     * @param {GridTile} tile 
     */
    renderTile(tile) {
        if (!tile) {
            return;
        }

        ////////////////////////////////////////////////////////////////////////////////
        // 100% AMBIENT LIGHTING - NO SHADOWS, NO TILE VISIBILITY STUFF
        ////////////////////////////////////////////////////////////////////////////////

        // const lighting = tile.lighting;
        // lighting.calculateShadowColor(); // <-- side effects....
        // this.projection.worldToScreen(tile.origin, this.screenCoordinate);

        // if (tile.open) {
        //     this.renderSprite(tile.backgroundSprite, this.screenCoordinate);
            
        //     if (tile.fixtureDescription) {
        //         // See if we need to lazily configure the sprite
        //         if (!tile.fixtureDescription.sprite) {
        //             tile.fixtureDescription.sprite = this.sprites.staticFixtureSpritesheet.getSprite(tile.fixtureDescription.spriteName);
        //         }

        //         this.renderSprite(tile.fixtureDescription.sprite, this.screenCoordinate);
        //     }
            
        //     if (tile.fixtureDescription && tile.fixtureDescription.sprite) {
        //         this.renderSprite(tile.fixtureDescription.sprite, this.screenCoordinate);
        //     }
        // }
        // else { 
        //     this.renderSprite(tile.sprite, this.screenCoordinate);
        // }


        ////////////////////////////////////////////////////////////////////////////

        // Some tiles do not support lighting: Sky, Grass (because they are expected to be in full sunlight)
        if (!tile.tileType.lightingSupported) {

            // Sky tiles and grass tiles are rendered even if the character has never seen them.

            this.projection.worldToScreen(tile.origin, this.screenCoordinate);

            if (tile.open) {
                this.renderSprite(tile.backgroundSprite, this.screenCoordinate);
            }
            else { 
                this.renderSprite(tile.sprite, this.screenCoordinate);
            }
        }
        else {
            const lighting = tile.lighting;
            if (!lighting.everVisibleToCharacter) {
                return;
            }

            this.projection.worldToScreen(tile.origin, this.screenCoordinate);

            const shadowColor = lighting.calculateShadowColor();

            // Shadow alpha:
            // a = 0: The tile is completely in the shadows and should be black.
            // a = 1: The tile is completely lighted and has no shadow.

            // Make sure the tile isn't completely black
            // Only render the tile sprite if there is at least some light being cast upon it.
            if (shadowColor.a > 0) {

                if (tile.open) {
                    this.renderSprite(tile.backgroundSprite, this.screenCoordinate);

                    if (tile.fixtureDescription) {
                        // See if we need to lazily configure the sprite
                        if (!tile.fixtureDescription.sprite) {
                            // TODO this completely sucks. can we make this not be so terrible?
                            tile.fixtureDescription.sprite = this.sprites.staticFixtureSpritesheet.getSprite(tile.fixtureDescription.spriteName);
                        }

                        this.renderSprite(tile.fixtureDescription.sprite, this.screenCoordinate);
                    }
                }
                else { 
                    this.renderSprite(tile.sprite, this.screenCoordinate);
                }

                ////////////////////////////////////////
                // Tile Overlay:  Cracks
                ////////////////////////////////////////

                if (!tile.open && tile.healthPercent != 100) {
                    const damageSprite = this.tileDamageSpriteOverlay.getDamageSprite(tile);
                    if (damageSprite) {
                        this.context.globalAlpha = 0.7;
                        this.renderSprite(damageSprite, this.screenCoordinate);
                        this.context.globalAlpha = 1;
                    }
                }

                ////////////////////////////////////////
                // Tile Overlay: Upgrade and Ore
                ////////////////////////////////////////

                if (!tile.open && tile.tileType.rarityModifier !== TileTypeRarity.COMMON) {

                    if (tile.tileType.rarityModifier === TileTypeRarity.UPGRADE) {
                        ////////////////////////////////////////
                        // Tile Overlay:  Upgrade
                        ////////////////////////////////////////

                        const upgradeSprite = this.upgradeSpriteOverlay.getCurrentSpriteDefault();
                        if (upgradeSprite) {
                            this.renderSprite(upgradeSprite, this.screenCoordinate);
                        }
                    }
                    else {
                        ////////////////////////////////////////
                        // Tile Overlay:  Ore
                        ////////////////////////////////////////

                        const oreSprite = this.oreSpriteOverlay.getOreSprite(tile);
                        if (oreSprite) {
                            this.renderSprite(oreSprite, this.screenCoordinate);                            
                        }

                        // TODO POINT SPRITE OVERLAY
                    }
                }
                    
                ///////////////////////////////////////////
                // Render a shadow over the tile 
                ///////////////////////////////////////////
        
                // Don't render the shadow if the tile is completely in the light
                if (shadowColor.a < 1) {
                    this.context.fillStyle = shadowColor.getFillColor();
                    this.context.fillRect(this.screenCoordinate.x, this.screenCoordinate.y, this.scaledSize, this.scaledSize);
                }
            }
        }

        if (this.state.gameOptions.crewBrainDebugEnabled) {
            this.renderTileDebug(tile);
        }
    }

    /**
     * @param {GridTile} tile 
     * @param {string} color
     */
    renderTileWire(tile, color) {
        if (!tile) {
            return;
        }


        this.screenCoordinate.copy(tile.origin);
        this.screenCoordinate.addXY(2, 2);

        this.screenCoordinate2.copy(tile.origin);
        this.screenCoordinate2.addXY(Settings.tile.size - 2, Settings.tile.size - 2);

        this.projection.worldToScreen(this.screenCoordinate, this.screenCoordinate);
        this.projection.worldToScreen(this.screenCoordinate2, this.screenCoordinate2);

        if (tile.open) {
            this.context.strokeStyle = color;
            this.context.lineWidth = 2;
            this.context.strokeRect(this.screenCoordinate.x, this.screenCoordinate.y, this.screenCoordinate2.x - this.screenCoordinate.x, this.screenCoordinate2.y - this.screenCoordinate.y);
        }
        else {
            this.context.fillStyle = color;
            this.context.fillRect(this.screenCoordinate.x, this.screenCoordinate.y, this.screenCoordinate2.x - this.screenCoordinate.x, this.screenCoordinate2.y - this.screenCoordinate.y);
        }
    }

    /**
     * @private
     * @param {GridTile} tile 
     */
    renderTileWireframe(tile) {
        if (!tile) {
            return;
        }

        // Some tiles do not support lighting: Sky, Grass (because they are expected to be in full sunlight)
        if (!tile.tileType.lightingSupported) {

            if (tile.open) {
                /////////this.renderTileWire(tile, '#4444');
                this.renderTileWire(tile, tile.tileType.openColor);
            }
            else { 
                //////this.renderTileWire(tile, '#AAF4');
                this.renderTileWire(tile, tile.tileType.closedColor);
            }
        }
        else {
            const lighting = tile.lighting;
            if (!lighting.everVisibleToCharacter) {
                return;
            }

            // // We need to calculate the shadow color - because of a side effect
            // const shadowColor = lighting.calculateShadowColor();

            if (tile.open) {
                ////this.renderTileWire(tile, '#4444');
                this.renderTileWire(tile, tile.tileType.openColor);
            }
            else { 

                // COLORS:
                // GOLD: FFD700
                // UPGRADE:  073f42   <-- CHEATED can't use black
                // ORE: 3d1b6d
                // RUBY: cc1827

                if (!tile.open && tile.tileType.rarityModifier !== TileTypeRarity.COMMON) {
                    if (tile.tileType.rarityModifier === TileTypeRarity.UPGRADE) {
                        this.renderTileWire(tile, '#073f42');
                    }
                    else if (tile.tileType.rarityModifier === TileTypeRarity.GOLD) {
                        this.renderTileWire(tile, '#FFD700');
                    }
                    else if (tile.tileType.rarityModifier === TileTypeRarity.ORE) {
                        this.renderTileWire(tile, '#3d1b6d');
                    }
                    else if (tile.tileType.rarityModifier === TileTypeRarity.RUBY) {
                        this.renderTileWire(tile, '#cc1827');
                    }
                    else {
                        this.renderTileWire(tile, '#AAF4');
                    }
                }
                else {
                    this.renderTileWire(tile, tile.tileType.closedColor);
                }

            }


            // this.projection.worldToScreen(tile.origin, this.screenCoordinate);


            // // Shadow alpha:
            // // a = 0: The tile is completely in the shadows and should be black.
            // // a = 1: The tile is completely lighted and has no shadow.

            // // Make sure the tile isn't completely black
            // // Only render the tile sprite if there is at least some light being cast upon it.
            // if (shadowColor.a > 0) {

            //     if (tile.open) {
            //         this.renderSprite(tile.backgroundSprite, this.screenCoordinate);

            //         if (tile.fixtureDescription) {
            //             // See if we need to lazily configure the sprite
            //             if (!tile.fixtureDescription.sprite) {
            //                 // TODO this completely sucks. can we make this not be so terrible?
            //                 tile.fixtureDescription.sprite = this.sprites.staticFixtureSpritesheet.getSprite(tile.fixtureDescription.spriteName);
            //             }

            //             this.renderSprite(tile.fixtureDescription.sprite, this.screenCoordinate);
            //         }
            //     }
            //     else { 
            //         this.renderSprite(tile.sprite, this.screenCoordinate);
            //     }

            //     ////////////////////////////////////////
            //     // Tile Overlay:  Cracks
            //     ////////////////////////////////////////

            //     if (!tile.open && tile.healthPercent != 100) {
            //         const damageSprite = this.tileDamageSpriteOverlay.getDamageSprite(tile);
            //         if (damageSprite) {
            //             this.context.globalAlpha = 0.7;
            //             this.renderSprite(damageSprite, this.screenCoordinate);
            //             this.context.globalAlpha = 1;
            //         }
            //     }

            //     ////////////////////////////////////////
            //     // Tile Overlay: Upgrade and Ore
            //     ////////////////////////////////////////

            //     if (!tile.open && tile.tileType.rarityModifier !== TileTypeRarity.COMMON) {

            //         if (tile.tileType.rarityModifier === TileTypeRarity.UPGRADE) {
            //             ////////////////////////////////////////
            //             // Tile Overlay:  Upgrade
            //             ////////////////////////////////////////

            //             const upgradeSprite = this.upgradeSpriteOverlay.getCurrentSpriteDefault();
            //             if (upgradeSprite) {
            //                 this.renderSprite(upgradeSprite, this.screenCoordinate);
            //             }
            //         }
            //         else {
            //             ////////////////////////////////////////
            //             // Tile Overlay:  Ore
            //             ////////////////////////////////////////

            //             const oreSprite = this.oreSpriteOverlay.getOreSprite(tile);
            //             if (oreSprite) {
            //                 this.renderSprite(oreSprite, this.screenCoordinate);                            
            //             }

            //             // TODO POINT SPRITE OVERLAY
            //         }
            //     }
                    
            //     ///////////////////////////////////////////
            //     // Render a shadow over the tile 
            //     ///////////////////////////////////////////
        
            //     // Don't render the shadow if the tile is completely in the light
            //     if (shadowColor.a < 1) {
            //         this.context.fillStyle = shadowColor.getFillColor();
            //         this.context.fillRect(this.screenCoordinate.x, this.screenCoordinate.y, this.scaledSize, this.scaledSize);
            //     }
            // }
        }

        if (this.state.gameOptions.crewBrainDebugEnabled) {
            this.renderTileDebug(tile);
        }
    }


    /**
     * @private
     * @param {GridTile} tile 
     */
    renderTileDebug(tile) {

        // const left = tile.getNeighborLeft();
        // const right = tile.getNeighborRight();
        // const top = tile.getNeighborTop();
        // const bottom = tile.getNeighborBottom();

        // if (!tile.open && tile.tileType.rarityModifier > 1) {
        //     this.context.fillStyle = "white";
        //     //const summary = 'type/sunlit/vis'
        //     const summary = (tile.lighting.sunlightFull ? 's' : 'n') + '/' + (tile.lighting.everVisibleToCharacter ? 'v' : 'n') + '/' + tile.mineTileIndex;
    
        //     this.screenCoordinate.copy(tile.origin);
        //     this.screenCoordinate.addXY(4, 4);
        //     this.projection.worldToScreen(this.screenCoordinate, this.screenCoordinate);        
        //     this.context.fillText(summary, this.screenCoordinate.x, this.screenCoordinate.y); 
        // }


        // ////////////////////////////////////
        // // Left
        // ////////////////////////////////////

        // let leftText = 'X';
        // if (!left) {
        //     leftText = '0';
        // }
        // else if (tile == left.getNeighborRight()) {
        //     leftText = '';
        // }

        // this.context.fillText(leftText, this.screenCoordinate.x + 10, this.screenCoordinate.y + (this.scaledSize / 2));

        // ////////////////////////////////////
        // // Right
        // ////////////////////////////////////

        // let rightText = 'X';
        // if (!right) {
        //     rightText = '0';
        // }
        // else if (tile == right.getNeighborLeft()) {
        //     rightText = '';
        // }

        // this.context.fillText(rightText, this.screenCoordinate.x + this.scaledSize - 15, this.screenCoordinate.y + (this.scaledSize / 2));

        // ////////////////////////////////////
        // // Top
        // ////////////////////////////////////

        // let topText = 'X';
        // if (!top) {
        //     topText = '0';
        // }
        // else if (tile == top.getNeighborBottom()) {
        //     topText = '';
        // }

        // this.context.fillText(topText, this.screenCoordinate.x + (this.scaledSize / 2), this.screenCoordinate.y + 10);


        // ////////////////////////////////////
        // // Bottom
        // ////////////////////////////////////

        // let bottomText = 'X';
        // if (!bottom) {
        //     bottomText = '0';
        // }
        // else if (tile == bottom.getNeighborTop()) {
        //     bottomText = '';
        // }

        // this.context.fillText(bottomText, this.screenCoordinate.x + (this.scaledSize / 2), this.screenCoordinate.y + this.scaledSize - 10);


        const vectorFieldNumber = this.vectorField.vectorFieldNumber;
                
        // const score = tile.pathData.getVectorFieldScore(vectorFieldNumber);
        // if (score !== -1) {
        //     this.context.fillStyle = this.vectorField.isUpgradeVisible() ? 'yellow' : "white";
        //     this.context.fillText("" + score, this.screenCoordinate.x + 10, this.screenCoordinate.y + 10);
        // }


        const mineScore = tile.pathData.getMineScore(vectorFieldNumber);

        if (mineScore === VectorFieldScores.UNSET) {
            return;
        }

        this.screenCoordinate.copy(tile.origin);
        this.screenCoordinate.addXY(Settings.tile.quarterSize, Settings.tile.quarterSize);

        this.screenCoordinate2.copy(tile.origin);
        this.screenCoordinate2.addXY(Settings.tile.threeQuarterSize, Settings.tile.threeQuarterSize);

        this.projection.worldToScreen(this.screenCoordinate, this.screenCoordinate);
        this.projection.worldToScreen(this.screenCoordinate2, this.screenCoordinate2);

        // if (mineScore === VectorFieldScores.IN_RANGE_OPEN) {
        //     this.context.fillStyle = '#4443';
        // }
        if (mineScore === VectorFieldScores.IN_RANGE_UNSEEN) {
            this.context.fillStyle = '#8665';
        }
        else if (mineScore === VectorFieldScores.WORTHLESS) {
            this.context.fillStyle = '#8885';
        }

        this.context.fillRect(this.screenCoordinate.x, this.screenCoordinate.y, this.screenCoordinate2.x - this.screenCoordinate.x, this.screenCoordinate2.y - this.screenCoordinate.y);
    }


    /**
     * @private
     */
    renderSkills() {

        const skills = this.state.skillTreeGraph.skills;
        for (let i = 0; i < skills.length; i++) {
            /**
             * @type {SkillType}
             */
            const skill = skills[i];
            if (!skill.isUnlocked()) {
                continue;
            }

            const renderer = skill.getRenderer();
            if (renderer) {
                renderer.renderSkill(this.context, this.projection);
            }
        }
    }

    /**
     * @private
     */
    renderEffects() {
        this.renderSpriteEffects();
        // area effects
        // travel effects
    }

    /**
     * @private
     */
    renderSpriteEffects() {
        const effects = this.state.effectManager.spriteEffects;
        if (effects.length === 0) {
            return;
        }

        this.renderEffectList(effects);
    }

    /**
     * @private
     * @param {Effect[]} effects
     */
    renderEffectList(effects) {
        for (let i = 0; i < effects.length; i++) {
            const effect = effects[i];
            if (!effect || effect.isEffectFinished() || effect.isNotStarted()) {
                continue;
            }

            // Sometimes a sprite can be null. This is fine.
            const sprite = effect.getSprite();
            if (!sprite) {
                continue;
            }

            const effectPosition = effect.getPosition();

            if (this.projection.isVisible(effectPosition, Settings.tile.size)) {
                this.projection.worldToScreen(effectPosition, this.screenCoordinate);
                this.renderScaledSprite(sprite, this.screenCoordinate);
            }
        }
    }

    /**
     * @private
     * @param {Sprite} sprite
     * @param {Vector} canvasCoordinate The canvas coordinate.
     */
    renderScaledSprite(sprite, canvasCoordinate) {
        if (sprite) {
            // canvas origin: top left corner. y-coordinate increases downwards.

            const spriteSize = sprite.getSize(); // Use the actual sprite size instead of the fixed 16px size that most sprites use.

            this.context.drawImage(sprite.getImage(),
                sprite.spriteX, sprite.spriteY,          // coords of sprite in sheet (top left corner)
                spriteSize, spriteSize,                  // width/height of sprite
                canvasCoordinate.x, canvasCoordinate.y,  // canvas coords to render sprite (top left corner)
                this.scaledSize, this.scaledSize);       // width/height of rendered sprite
        }
    }

    /**
     * @private
     * @param {Sprite} sprite
     * @param {Vector} canvasCoordinate The canvas coordinate.
     */
    renderSprite(sprite, canvasCoordinate) {
        if (sprite) {
            // canvas origin: top left corner. y-coordinate increases downwards.

            this.context.drawImage(sprite.getImage(),
                sprite.spriteX, sprite.spriteY,          // coords of sprite in sheet (top left corner)
                this.spriteSize, this.spriteSize,        // width/height of sprite
                canvasCoordinate.x, canvasCoordinate.y,  // canvas coords to render sprite (top left corner)
                this.scaledSize, this.scaledSize);       // width/height of rendered sprite
        }
    }

}